import { Link } from "react-router-dom";
import { Shield, Users, CheckCircle } from "lucide-react";
import { useSiteContact } from "@/hooks/use-site-config";
import BrandLogo from "@/components/BrandLogo";

interface PersonalLoanFormLayoutProps {
  children: React.ReactNode;
  currentStep: number;
  totalSteps: number;
}

const PersonalLoanFormLayout = ({ children, currentStep, totalSteps }: PersonalLoanFormLayoutProps) => {
  const progressWidth = `${(currentStep / totalSteps) * 100}%`;
  const contact = useSiteContact();
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <BrandLogo />
          </Link>
          <a href={`tel:${contact.phone.replace(/[^0-9+]/g, "")}`} className="text-muted-foreground hover:text-foreground transition-colors">
            {contact.phone}
          </a>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="w-full bg-muted h-1">
        <div 
          className="bg-primary h-1 transition-all duration-300" 
          style={{ width: progressWidth }}
        />
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Form Section */}
            <div className="lg:col-span-3 bg-card rounded-2xl border border-border p-8">
              {children}
            </div>

            {/* Benefits Sidebar */}
            <div className="lg:col-span-2 bg-accent/30 rounded-2xl p-6">
              <h2 className="text-xl font-bold text-foreground mb-6">
                See your personalized offers in minutes
              </h2>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">No impact to your credit</h3>
                    <p className="text-sm text-muted-foreground">
                      Checking rates won't affect your credit score.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Trusted by millions</h3>
                    <p className="text-sm text-muted-foreground">
                      Join countless customers who found their perfect loan.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">Fast & easy process</h3>
                    <p className="text-sm text-muted-foreground">
                      Get matched with offers in just a few minutes.
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-xs text-muted-foreground mt-8">
                Filling out the form won't affect your credit score.
              </p>
              <a
                href="/advertising-disclosure"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-muted-foreground underline hover:text-foreground mt-2 inline-block"
              >
                Advertiser Disclosure
              </a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PersonalLoanFormLayout;
